import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Inbox, Maximize2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { useActiveInboxAccounts } from "@/hooks/inbox/useConversations";
import { useInboxRealtime } from "@/hooks/inbox/useInboxRealtime";
import { InboxProvider, useInboxUI } from "./InboxProvider";
import { ConversationList } from "./ConversationList";
import { MessageThread } from "./MessageThread";
import { useFilteredConversations } from "./useFilteredConversations";

interface QuickPeekDrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

const PeekContent: React.FC<QuickPeekDrawerProps> = ({ open, onOpenChange }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { selectedId, setSelectedId } = useInboxUI();
	const { hasActiveAccounts, isLoading: accountsLoading } = useActiveInboxAccounts();
	const { allConversations } = useFilteredConversations(hasActiveAccounts);
	useInboxRealtime(open && hasActiveAccounts);

	const selectedConversation = allConversations.find((c) => c.id === selectedId);

	const openFullInbox = () => {
		onOpenChange(false);
		navigate(selectedId ? `/inbox?c=${encodeURIComponent(selectedId)}` : "/inbox");
	};

	return (
		<div className="flex h-full min-h-0 flex-col overflow-hidden bg-background">
			<header className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2.5">
				<Inbox className="h-4 w-4 text-primary" />
				<h2 className="text-sm font-semibold text-foreground">{t("inbox.title", "Inbox")}</h2>
				<div className="ml-auto flex items-center gap-1">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-8 w-8 text-muted-foreground"
								onClick={openFullInbox}
								aria-label={t("inbox.openFull", "Open full inbox")}
							>
								<Maximize2 className="h-4 w-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="bottom">{t("inbox.openFull", "Open full inbox")}</TooltipContent>
					</Tooltip>
					<Button
						variant="ghost"
						size="icon"
						className="h-8 w-8 text-muted-foreground"
						onClick={() => onOpenChange(false)}
						aria-label={t("inbox.close", "Close")}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>
			</header>

			<div className="min-h-0 flex-1">
				{selectedConversation ? (
					<MessageThread
						conversation={selectedConversation}
						toolbarId="inbox-peek-email-toolbar"
						onBack={() => setSelectedId(null)}
					/>
				) : (
					<ConversationList hasActiveAccounts={hasActiveAccounts || accountsLoading} />
				)}
			</div>
		</div>
	);
};

/**
 * Slim quick-peek inbox: one pane at a time (list ⇄ thread), summoned from
 * the header Mail button without leaving the current page. Shares all
 * components and caches with the full /inbox page.
 */
export const QuickPeekDrawer: React.FC<QuickPeekDrawerProps> = ({ open, onOpenChange }) => {
	const isMobile = useIsMobile();

	if (isMobile) {
		return (
			<Sheet open={open} onOpenChange={onOpenChange}>
				<SheetContent side="right" className="w-full max-w-full p-0 sm:max-w-md [&>button]:hidden">
					<SheetTitle className="sr-only">Inbox</SheetTitle>
					<InboxProvider variant="peek">
						<PeekContent open={open} onOpenChange={onOpenChange} />
					</InboxProvider>
				</SheetContent>
			</Sheet>
		);
	}

	if (!open) return null;

	return (
		<InboxProvider variant="peek">
			<PeekContent open={open} onOpenChange={onOpenChange} />
		</InboxProvider>
	);
};
